import moment from 'moment';
import { Price } from 'shared-types/Common';
import { ConfigurationGroupType, SpaPackageType, TimeSlotType } from 'shared-types/SpaPackage';
import type { DayAvailability, OptionsPanelConfig } from 'voyager';
import { formatTime } from '@common/helpers/formatTime';

import { isBookableGroup, isBookableOption } from '../configGroupBookability';
import { SpaPackageFormValues } from '../SpaPackageForm.schema';
import { isStartTimeAtCapacity } from './timeslotCapacity';
import { isStartTimeTakenByOtherOption, isTreatmentOption, OptionRef } from './timeslotClash';

type GuestConfigGroups = SpaPackageFormValues['guests'][number]['configurationGroups'];

export function buildInitialConfigurationGroups(
  groups: ConfigurationGroupType[]
): NonNullable<GuestConfigGroups> {
  const result: NonNullable<GuestConfigGroups> = {};
  for (const group of groups) {
    if (!shouldRenderConfigGroup(group)) continue;
    const bookableOptions = group.options.filter(isBookableOption);
    if (bookableOptions.length === 0) continue;
    result[`group_${group.id}`] = bookableOptions.map((opt) => ({
      configurationGroupId: group.id,
      optionId: opt.id,
      isSelected: !group.supportsSelections,
      timeslot: null,
    }));
  }
  return result;
}

export function mapTimeslotsToDayAvailability(
  timeslots: Record<string, TimeSlotType[]> | undefined,
  startDate: string
): DayAvailability[] {
  if (!timeslots) return [];
  const days: DayAvailability[] = [];
  for (let i = 0; i < 7; i++) {
    const date = moment(startDate).add(i, 'days').format('YYYY-MM-DD');
    const slots = timeslots[date];
    days.push({
      date,
      available: !!slots && slots.length > 0,
    });
  }
  return days;
}

export interface OptionsPanelLabels {
  includedTitle: string;
  includedSubtitle: string;
  optionsTitle: string;
  optionsSubtitle: string;
  optionsSubtitleMultiple: string;
  formatSelectionCount: (selected: number, required: number) => string;
}

export function isForcedConfigGroup(group: ConfigurationGroupType): boolean {
  if (!group.supportsSelections) return true;
  const optionCount = group.options.length;
  const min = group.minimumSelections ?? 1;
  return optionCount > 0 && min >= optionCount;
}

export function shouldRenderConfigGroup(group: ConfigurationGroupType): boolean {
  return (
    !group.isExtras &&
    group.isRequired &&
    isBookableGroup(group) &&
    group.options.some(isBookableOption)
  );
}

export interface OptionsPanelErrorMessages {
  optionRequired: string;
  timeslotRequired: string;
  noTimesRemaining: string;
}

export type ConfigGroupErrors =
  | ({ message?: string } & Record<number, { message?: string } | undefined>)
  | undefined;

export function mapConfigGroupToOptionsPanel(
  rawGroup: ConfigurationGroupType,
  guestSelections: GuestConfigGroups,
  labels: OptionsPanelLabels,
  groupErrors?: ConfigGroupErrors,
  errorMessages?: OptionsPanelErrorMessages,
  requireTimeslots = true,
  bookedStartTimes: Map<string, OptionRef[]> = new Map(),
  startTimesHeldByOtherGuests: Map<string, number> = new Map()
): OptionsPanelConfig | null {
  if (!shouldRenderConfigGroup(rawGroup)) return null;

  const group: ConfigurationGroupType = {
    ...rawGroup,
    options: rawGroup.options.filter(isBookableOption),
  };
  if (group.options.length === 0) return null;

  const groupKey = `group_${group.id}`;
  const selections = guestSelections?.[groupKey] as
    | { optionId: number; isSelected: boolean; timeslot: { startTime: string } | null }[]
    | undefined;

  const hasGroupError = !!groupErrors?.message;
  const getSelectionError = (idx: number): boolean => !!groupErrors?.[idx]?.message;

  type Option = ConfigurationGroupType['options'][number];

  const offeredTimeslots = (opt: Option): TimeSlotType[] =>
    Array.isArray(opt.timeslots) ? opt.timeslots : [];

  const optionRefFor = (opt: Option): OptionRef => ({
    configurationGroupId: group.id,
    optionId: opt.id,
  });

  const isTimeTakenByOtherGuests = (opt: Option, timeslot: TimeSlotType) =>
    isStartTimeAtCapacity(startTimesHeldByOtherGuests, optionRefFor(opt), timeslot);

  const isTimeClosedToGuest = (opt: Option, timeslot: TimeSlotType) =>
    isTimeTakenByOtherGuests(opt, timeslot) ||
    (isTreatmentOption(opt) &&
      isStartTimeTakenByOtherOption(bookedStartTimes, timeslot.startTime, optionRefFor(opt)));

  const hasNoSpaceLeftForGuest = (opt: Option) => {
    const offered = offeredTimeslots(opt);
    if (offered.length === 0) return false;

    return (
      offered.every((timeslot) => isTimeClosedToGuest(opt, timeslot)) &&
      offered.some((timeslot) => isTimeTakenByOtherGuests(opt, timeslot))
    );
  };

  const isAwaitingASpace = (opt: Option, idx: number) => {
    const selection = selections?.[idx];
    if (group.supportsSelections && !selection?.isSelected) return false;
    if (selection?.timeslot) return false;
    return hasNoSpaceLeftForGuest(opt);
  };

  const mapOptionBase = (opt: Option) => ({
    id: String(opt.id),
    label: opt.name || '',
    imageUrl: opt.image?.url,
    imageAlt: opt.name,
    timeslots: offeredTimeslots(opt).map((ts) => ({
      value: ts.startTime,
      label: formatTime(ts.startTime),
      disabled: isTimeClosedToGuest(opt, ts),
    })),
    disabled: opt.timeslots?.length === 0,
  });

  const mapVisibleOption = (opt: Option) => {
    const base = mapOptionBase(opt);
    return requireTimeslots ? base : { ...base, timeslots: [] };
  };

  if (!group.supportsSelections) {
    const timeslotErrors: Record<string, string> = {};
    const timeslotNotices: Record<string, string> = {};
    if (errorMessages) {
      group.options.forEach((opt, idx) => {
        if (isAwaitingASpace(opt, idx)) {
          timeslotNotices[String(opt.id)] = errorMessages.noTimesRemaining;
          return;
        }
        if (getSelectionError(idx)) {
          timeslotErrors[String(opt.id)] = errorMessages.timeslotRequired;
        }
      });
    }
    return {
      type: 'all' as const,
      title: labels.includedTitle,
      subtitle: labels.includedSubtitle,
      options: group.options.map((opt, idx) => ({
        ...mapVisibleOption(opt),
        selectedTimeslot: selections?.[idx]?.timeslot?.startTime,
      })),
      timeslotErrors: Object.keys(timeslotErrors).length > 0 ? timeslotErrors : undefined,
      timeslotNotices: Object.keys(timeslotNotices).length > 0 ? timeslotNotices : undefined,
    };
  }

  const min = group.minimumSelections ?? 1;
  const max = group.maximumSelections ?? 1;
  const required = group.maximumSelections ?? min;

  if (max > 1 || min > 1) {
    const selectedCount = selections?.filter((s) => s?.isSelected).length ?? 0;
    const timeslotErrors: Record<string, string> = {};
    const timeslotNotices: Record<string, string> = {};
    if (errorMessages) {
      group.options.forEach((opt, idx) => {
        if (isAwaitingASpace(opt, idx)) {
          timeslotNotices[String(opt.id)] = errorMessages.noTimesRemaining;
          return;
        }
        if (getSelectionError(idx)) {
          timeslotErrors[String(opt.id)] = errorMessages.timeslotRequired;
        }
      });
    }
    return {
      type: 'multi' as const,
      title: labels.optionsTitle,
      subtitle: labels.optionsSubtitleMultiple,
      selectionCountText: labels.formatSelectionCount(selectedCount, required),
      options: group.options.map((opt, idx) => {
        const selection = selections?.[idx];
        const isSelected = !!selection?.isSelected;
        return {
          ...mapVisibleOption(opt),
          isSelected,
          selectedTimeslot: isSelected ? selection?.timeslot?.startTime : undefined,
        };
      }),
      maximumSelections: max,
      optionError: hasGroupError ? errorMessages?.optionRequired : undefined,
      timeslotErrors: Object.keys(timeslotErrors).length > 0 ? timeslotErrors : undefined,
      timeslotNotices: Object.keys(timeslotNotices).length > 0 ? timeslotNotices : undefined,
    };
  }

  const selectedEntry = selections?.find((s) => s?.isSelected);
  const selectedEntryIndex = selections?.findIndex((s) => s?.isSelected) ?? -1;
  const selectedOption = group.options.find((opt) => opt.id === selectedEntry?.optionId);
  const selectionHasTimeslotError =
    selectedEntryIndex >= 0 && getSelectionError(selectedEntryIndex);
  const showsNoTimesNotice =
    !!selectedOption && isAwaitingASpace(selectedOption, selectedEntryIndex);

  return {
    type: 'single' as const,
    title: labels.optionsTitle,
    subtitle: labels.optionsSubtitle,
    selectionCountText: labels.formatSelectionCount(selectedEntry ? 1 : 0, required),
    options: group.options.map(mapVisibleOption),
    selectedOptionId: selectedEntry ? String(selectedEntry.optionId) : undefined,
    selectedTimeslot: selectedEntry?.timeslot?.startTime,
    optionError: hasGroupError ? errorMessages?.optionRequired : undefined,
    timeslotNotice: showsNoTimesNotice ? errorMessages?.noTimesRemaining : undefined,
    timeslotError:
      !showsNoTimesNotice && selectionHasTimeslotError
        ? errorMessages?.timeslotRequired
        : undefined,
  };
}

export function calculateTotalPrice(entityPrice: Price, count: number, extras: number): Price {
  return {
    ...entityPrice,
    amount: entityPrice.amount * count + extras,
  };
}

export function clampGuestCountToPackage(
  entity: Pick<SpaPackageType, 'numberOfGuests' | 'maximumGuestsPerBasket'>,
  requestedGuests: number | undefined
): number {
  const step = Math.max(1, entity.numberOfGuests || 1);
  // An unset basket maximum means no cap, the same as the guest stepper reads it. Falling back to
  // the package size instead would clamp the searched party down to one.
  const max = entity.maximumGuestsPerBasket || Infinity;
  const clamped = Math.max(step, Math.min(max, requestedGuests || step));
  return Math.max(step, Math.floor(clamped / step) * step);
}
